import React, { useState, useEffect } from 'react';
import { supabase, apiFetch } from '../../lib/supabase';
import { F, C, btn, inp } from '../../config/styles';

export function LoginScreen({ tenant }) {
  const [email, setEmail]       = useState('');
  const [sent, setSent]         = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Authorisation is enforced server-side (a browser check is bypassable via the
    // public anon key). This endpoint checks the email against the tenant's
    // allow-list — domain match, explicit invite, or existing member — and, only
    // if authorised, provisions the auth user. Supabase self-signup is OFF, so an
    // unauthorised email can't get a link any other way either.
    try {
      const resp = await apiFetch('/api/auth/request-link', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim(), subdomain: tenant?.subdomain }),
      });
      if (resp.status === 403) {
        setLoading(false);
        setError("This email isn't authorised for this workspace.");
        return;
      }
      if (resp.status === 429) {
        setLoading(false);
        setError('Too many attempts. Please wait a little and try again.');
        return;
      }
      if (!resp.ok) {
        setLoading(false);
        setError('Something went wrong. Please try again.');
        return;
      }
    } catch (_) {
      setLoading(false);
      setError('Unable to reach the server. Check your connection and try again.');
      return;
    }

    // Authorised: the user now exists, so ask Supabase to send the magic link.
    const { error: err } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: window.location.origin, shouldCreateUser: false },
    });
    setLoading(false);
    if (err) { setError(err.message); return; }
    setSent(true);
    setCooldown(60);
  };

  return (
    <div style={{ minHeight: '100vh', background: C.fa, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F.body }}>
      <div style={{ background: C.white, border: `1px solid ${C.e8}`, borderRadius: 12, padding: 40, width: 360 }}>
        <div style={{ fontFamily: F.display, fontSize: 22, color: C.text, marginBottom: 8 }}>Range Builder</div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 28 }}>Sign in with your work email</div>
        {sent ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>✉️</div>
            <div style={{ fontSize: 14, color: C.text, fontWeight: 600, marginBottom: 8 }}>Check your email</div>
            <div style={{ fontSize: 13, color: C.muted }}>We sent a magic link to <strong>{email}</strong></div>
            <button
              style={{ ...btn('ghost', { marginTop: 20, fontSize: 12, color: C.muted }), opacity: cooldown > 0 ? 0.5 : 1 }}
              disabled={cooldown > 0}
              onClick={() => setSent(false)}
            >
              {cooldown > 0 ? `Resend available in ${cooldown}s` : 'Resend or use a different email'}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 12 }}>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@yourbrand.com"
                required
                style={{ ...inp({ width: '100%', padding: '10px 12px', fontSize: 14, boxSizing: 'border-box' }) }}
              />
            </div>
            {error && <div style={{ fontSize: 12, color: '#e53e3e', marginBottom: 10 }}>{error}</div>}
            <button type="submit" disabled={loading || cooldown > 0} style={{ ...btn('primary', { width: '100%', padding: '10px 0', fontSize: 14, justifyContent: 'center' }) }}>
              {loading ? 'Sending…' : cooldown > 0 ? `Wait ${cooldown}s` : 'Send magic link'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
